import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { AiRecruiterService } from './ai-recruiter.service';
import { TalentService } from '../talent.service';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface Recommendation {
  candidate_id: number;
  first_name: string;
  last_name: string;
  primary_skill: string;
  city: string;
  region: string;
  reason: string;
  round: number;
  file_id?: string;
  filename?: string;
  status?: string;
  rejection_comment?: string;
  showRejectInput?: boolean;
}

@Component({
  selector: 'app-ai-recruiter',
  templateUrl: './ai-recruiter.component.html',
  styleUrls: ['./ai-recruiter.component.css'],
  standalone: false
})
export class AiRecruiterComponent implements OnInit, AfterViewChecked {
  @ViewChild('chatContainer', { static: false }) chatContainer: ElementRef;

  openRoles: any[] = [];
  selectedRoleId: number = null;
  selectedRole: any = null;

  modelOptions = [
    { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6', cost: '~$0.18 / Find' },
    { value: 'claude-opus-4-6',   label: 'Opus 4.6',   cost: '~$0.90 / Find' }
  ];
  selectedModel: string = 'claude-sonnet-4-6';
  get isAdmin(): boolean { return this.talentService.is_admin; }

  messages: ChatMessage[] = [];
  userInput: string = '';
  isLoading: boolean = false;
  isChatActive: boolean = false;
  isIntakeComplete: boolean = false;
  isResuming: boolean = false;
  isSaved: boolean = false;

  recommendations: Recommendation[] = [];
  isLoadingRecommendations: boolean = false;
  recommendationRound: number = 0;
  allActioned: boolean = false;
  showDetail: { [key: number]: boolean } = {};
  pdfShow: { [key: number]: boolean } = {};
  pdf: { [key: number]: any } = {};

  toggleDetail(candidateId: number) {
    this.showDetail[candidateId] = !this.showDetail[candidateId];
    this.pdfShow[candidateId] = false;
  }

  pdfToggle(rec: Recommendation) {
    const id = rec.candidate_id;
    if (this.pdfShow[id]) {
      this.pdfShow[id] = false;
      return;
    }
    this.talentService.getMyActiveCandidateResume(id).subscribe((result: any) => {
      const fileId = result['file_id'] || rec.file_id;
      if (!fileId || fileId === '-') return;
      this.showDetail[id] = false;
      this.talentService.getPDF(fileId).subscribe((data: any) => {
        this.pdf[id] = new Uint8Array(data);
        this.pdfShow[id] = true;
      });
    });
  }

  constructor(private aiRecruiterService: AiRecruiterService, private talentService: TalentService) {}

  ngOnInit() {
    this.aiRecruiterService.getOpenRoles().subscribe((roles) => {
      this.openRoles = roles;
    });
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  onRoleSelected() {
    if (!this.selectedRoleId) return;

    this.messages = [];
    this.isChatActive = false;
    this.isIntakeComplete = false;
    this.isResuming = false;
    this.isSaved = false;
    this.recommendations = [];
    this.allActioned = false;
    this.isLoading = true;

    this.aiRecruiterService.getRoleDetail(this.selectedRoleId).subscribe(
      (role) => {
        this.selectedRole = role;
        this.checkExistingIntake();
      },
      (err) => {
        console.error('Failed to load role detail:', err);
        this.isLoading = false;
      }
    );
  }

  checkExistingIntake() {
    this.aiRecruiterService.getIntakeSession(this.selectedRoleId).subscribe(
      (res) => {
        if (res.exists) {
          this.messages = res.messages || [];
          this.isIntakeComplete = false;
          this.isResuming = true;
          this.isChatActive = true;
          if (res.is_complete) {
            this.isSaved = true;
            this.loadExistingRecommendations();
          }
          this.resumeIntake();
        } else {
          this.startIntake();
        }
      },
      (err) => {
        console.error('Failed to check existing intake:', err);
        this.startIntake();
      }
    );
  }

  loadExistingRecommendations() {
    this.aiRecruiterService.getRecommendations(this.selectedRoleId).subscribe(
      (recs: any[]) => {
        if (!recs || recs.length === 0) return;
        // Track the latest round so checkAllActioned knows we've had recommendations
        this.recommendationRound = Math.max(...recs.map(r => r.round));
        // Only show pending recommendations — actioned ones are done
        this.recommendations = recs
          .filter(r => r.status === 'pending' || !r.status)
          .map(r => ({
            candidate_id: r.candidate_id,
            first_name: r.first_name,
            last_name: r.last_name,
            primary_skill: r.primary_skill_name || r.primary_skill,
            city: r.city,
            region: r.region,
            reason: r.reason || '',
            round: r.round,
            status: undefined,
            rejection_comment: r.rejection_comment,
            showRejectInput: false
          }));
        this.checkAllActioned();
      },
      (err) => console.error('Failed to load existing recommendations:', err)
    );
  }

  startIntake() {
    this.isChatActive = true;
    this.isLoading = true;

    this.aiRecruiterService.sendIntakeMessage(this.selectedRole, [], false).subscribe(
      (res) => {
        this.messages.push({ role: 'assistant', content: res.message });
        this.isIntakeComplete = res.is_complete;
        this.isLoading = false;
      },
      (err) => {
        console.error('Failed to start intake session:', err);
        this.isLoading = false;
      }
    );
  }

  resumeIntake() {
    this.isLoading = true;

    this.aiRecruiterService.sendIntakeMessage(this.selectedRole, this.messages, true).subscribe(
      (res) => {
        this.messages.push({ role: 'assistant', content: res.message });
        this.isIntakeComplete = res.is_complete;
        this.isLoading = false;
      },
      (err) => {
        console.error('Failed to resume intake session:', err);
        this.isLoading = false;
      }
    );
  }

  sendMessage() {
    const text = this.userInput.trim();
    if (!text || this.isLoading) return;

    if (text.toUpperCase() === 'DONE') {
      this.messages.push({ role: 'user', content: text });
      this.messages.push({ role: 'assistant', content: 'Got it — marking this intake as complete. Please save the conversation using the button below.' });
      this.userInput = '';
      this.isIntakeComplete = true;
      console.log('DONE detected — isIntakeComplete:', this.isIntakeComplete, 'isChatActive:', this.isChatActive);
      return;
    }

    this.messages.push({ role: 'user', content: text });
    this.userInput = '';
    this.isLoading = true;

    this.aiRecruiterService.sendIntakeMessage(this.selectedRole, this.messages, this.isResuming).subscribe(
      (res) => {
        this.messages.push({ role: 'assistant', content: res.message });
        this.isIntakeComplete = res.is_complete;
        console.log('AI response — is_complete:', res.is_complete, 'isIntakeComplete:', this.isIntakeComplete);
        this.isLoading = false;
      },
      (err) => {
        console.error('Failed to send intake message:', err);
        this.isLoading = false;
      }
    );
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  saveIntake() {
    this.aiRecruiterService.saveIntakeSession(this.selectedRoleId, this.messages, this.isIntakeComplete).subscribe(
      () => {
        this.isSaved = true;
      },
      (err) => {
        console.error('Failed to save intake:', err);
      }
    );
  }

  findCandidates() {
    this.isLoadingRecommendations = true;
    this.allActioned = false;

    this.aiRecruiterService.generateRecommendations(this.selectedRoleId, this.selectedRole, this.messages, this.selectedModel).subscribe(
      (res) => {
        const existingIds = new Set(this.recommendations.map(r => r.candidate_id));
        const newRecs = (res.candidates || [])
          .filter(c => !existingIds.has(c.candidate_id))
          .map(c => ({ ...c, showRejectInput: false }));
        this.recommendations = [...this.recommendations, ...newRecs];
        this.recommendationRound = res.round;
        this.isLoadingRecommendations = false;
        this.checkAllActioned();
      },
      (err) => {
        console.error('Failed to generate recommendations:', err);
        console.error('Error detail:', err.error);
        this.isLoadingRecommendations = false;
      }
    );
  }

  acceptCandidate(rec: Recommendation) {
    this.recommendations = this.recommendations.filter(r => r.candidate_id !== rec.candidate_id);
    this.checkAllActioned();

    this.aiRecruiterService.submitFeedback(this.selectedRoleId, rec.candidate_id, 'accepted').subscribe(
      () => {},
      (err) => console.error('Failed to submit feedback:', err)
    );

    this.talentService.addCandidateToRole({
      candidate_id: rec.candidate_id,
      role_details: { role_id: this.selectedRoleId, client: this.selectedRole ? this.selectedRole.client : '' }
    }).subscribe(
      () => {},
      (err) => console.error('Failed to add candidate to role:', err)
    );
  }

  showRejectInput(rec: Recommendation) {
    rec.showRejectInput = true;
  }

  submitRejection(rec: Recommendation) {
    if (!rec.rejection_comment || !rec.rejection_comment.trim()) return;
    rec.showRejectInput = false;
    this.aiRecruiterService.submitFeedback(this.selectedRoleId, rec.candidate_id, 'rejected', rec.rejection_comment).subscribe(
      () => {},
      (err) => console.error('Failed to submit rejection:', err)
    );
    this.recommendations = this.recommendations.filter(r => r.candidate_id !== rec.candidate_id);
    this.checkAllActioned();
  }

  checkAllActioned() {
    this.allActioned = this.recommendationRound > 0 && this.recommendations.length === 0;
  }

  private scrollToBottom() {
    if (this.chatContainer) {
      try {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      } catch (e) {}
    }
  }
}
